import {
  Home as HomeIcon,
  User as UserIcon,
  Zap as ZapIcon,
} from 'lucide-react';
import { createElement } from 'react';
import type { CSSProperties } from 'react';

const discoverIconMaskStyle: CSSProperties = {
  display: 'inline-block',
  backgroundColor: 'currentColor',
  maskImage: 'url(/svg/discover.svg)',
  maskSize: 'contain',
  maskRepeat: 'no-repeat',
  maskPosition: 'center',
  WebkitMaskImage: 'url(/svg/discover.svg)',
  WebkitMaskSize: 'contain',
  WebkitMaskRepeat: 'no-repeat',
  WebkitMaskPosition: 'center',
};

function DiscoverIcon({ className }: { className?: string }) {
  return createElement('span', {
    className,
    style: discoverIconMaskStyle,
    'aria-hidden': 'true',
  });
}

DiscoverIcon.defaultProps = {
  className: undefined,
};

type IconName = 'home' | 'discover' | 'now' | 'user';

export interface MobileBottomNavConfigItem {
  name: string;
  href?: string;
  icon: IconName;
  guestHref?: string;
  requireAuth?: boolean;
}

const iconRegistry: Record<IconName, any> = {
  home: HomeIcon,
  discover: DiscoverIcon,
  now: ZapIcon,
  user: UserIcon,
};

export const MOBILE_NAV_DEFAULTS: MobileBottomNavConfigItem[] = [
  { name: 'Home', href: '/', guestHref: '/discover', icon: 'home' },
  { name: 'Discover', href: '/discover', icon: 'discover' },
  { name: 'Now', href: '/now', icon: 'now' },
  { name: 'Profile', icon: 'user', requireAuth: false },
];

function isValidIconName(x: string): x is IconName {
  return ['home', 'discover', 'now', 'user'].includes(x);
}

function sanitizeConfig(input: unknown): MobileBottomNavConfigItem[] | null {
  if (!Array.isArray(input)) return null;
  const items = input
    .map((raw) => {
      if (!raw || typeof raw !== 'object') return null;
      const obj = raw as Record<string, unknown>;
      const name = typeof obj.name === 'string' ? obj.name : null;
      const href = typeof obj.href === 'string' ? obj.href : undefined;
      const guestHref =
        typeof obj.guestHref === 'string' ? obj.guestHref : undefined;
      const requireAuth =
        typeof obj.requireAuth === 'boolean' ? obj.requireAuth : undefined;
      const icon =
        typeof obj.icon === 'string' && isValidIconName(obj.icon)
          ? (obj.icon as IconName)
          : null;
      if (!name || !icon) return null;
      return { name, href, icon, guestHref, requireAuth };
    })
    .filter(Boolean) as MobileBottomNavConfigItem[];
  return items.length > 0 ? items : null;
}

export interface ResolvedNavItem {
  name: string;
  href?: string;
  icon: any;
  onClick?: () => void;
}

export function resolveMobileNavItems(
  session: unknown,
  profileClick: () => void
): ResolvedNavItem[] {
  let configured: MobileBottomNavConfigItem[] | null = null;
  try {
    if (typeof window !== 'undefined') {
      const fromLS = window.localStorage.getItem('mobileBottomNav');
      if (fromLS) configured = sanitizeConfig(JSON.parse(fromLS));
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - allow optional global override
      if (!configured && window.__MOBILE_NAV__) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        configured = sanitizeConfig(window.__MOBILE_NAV__);
      }
    }
  } catch {
    // ignore invalid config
  }
  const base = configured ?? MOBILE_NAV_DEFAULTS;
  const migratedBase = base
    .map((item) => {
      if (item.href === '/schedule' || item.href === '/news') {
        return { ...item, name: 'Now', href: '/now', icon: 'now' as const };
      }

      return item;
    })
    .filter((item) => item.href !== '/music');

  const hasNow = migratedBase.some((item) => item.href === '/now');
  const normalizedBase = hasNow
    ? migratedBase
    : [...migratedBase, { name: 'Now', href: '/now', icon: 'now' as const }];

  const dedupedBase = normalizedBase.reduce<MobileBottomNavConfigItem[]>(
    (acc, item) => {
      const exists = acc.some((existingItem) => {
        if (item.href && existingItem.href) {
          return existingItem.href === item.href;
        }

        return existingItem.name === item.name;
      });

      if (!exists) {
        acc.push(item);
      }

      return acc;
    },
    []
  );

  const profileItem = dedupedBase.find((item) => item.name === 'Profile');
  const nonProfileItems = dedupedBase.filter((item) => item.name !== 'Profile');
  const nowIndex = nonProfileItems.findIndex((item) => item.href === '/now');

  if (nowIndex === -1) {
    nonProfileItems.push({ name: 'Now', href: '/now', icon: 'now' });
  }

  const orderedBase = profileItem
    ? [...nonProfileItems, profileItem]
    : nonProfileItems;

  const resolved: ResolvedNavItem[] = orderedBase.map((item) => {
    const Icon = iconRegistry[item.icon];
    if (item.name === 'Profile') {
      return { name: item.name, icon: Icon, onClick: profileClick };
    }
    const { href } = item;
    return { name: item.name, icon: Icon, href };
  });
  return resolved;
}
